/*------------------------------------------------------------------------------
   About      : Typed service layer over proxypulse's insight blueprint
                (epic DA-697, II-FE-1)

                quill-fe ──► proxypulse ──► quill

                Insight calls go through `rc.bffClient`, never `cmsApiClient`:
                RBAC (`@require_feature()`) and audit live in proxypulse, and it
                forwards the end-user JWT to quill so the workflow guard can
                resolve a role. Calling quill directly bypasses all three.

                Every method returns `{ data?, error? }` and never throws — the
                house contract. `BffApiClient` returns a raw AxiosResponse on
                success and `{ error }` on failure; this layer normalises that
                into one envelope so feature code never branches on which.

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import { RunContextApp }             from 'framework/RCApp'
import { CreateInsightRequest,
         CreateInsightResponse,
         GenerateAlternativeRequest,
         GenerateAlternativeResponse,
         GetInsightsResponse,
         InsightBatchRequest,
         InsightBatchResponse,
         InsightCatalog,
         InsightReviewWorkflow,
         SemanticValidateResponse,
         TagRecommendationsRequest,
         TagRecommendationsResponse,
         TemplateTextValidation,
         UpdateAllRequest,
         UpdateAllResponse }         from '../types/insightTypes'
import { normalizeInsightCatalog }   from '../utils/insightCatalogHelpers'

export interface InsightApiResult<T> {
  data  ?: T
  error ?: { message?: string }
}

/**
 * Normalises what `BffApiClient` hands back.
 *
 * Two things this has to get right, and truthiness cannot tell them apart:
 *
 * 1. A `{ error }` object from the client's catch block has no `.data`.
 * 2. A 200 carrying the wrong envelope leaves `.data` set but every field
 *    undefined. Downstream that renders as an empty catalog / empty list —
 *    indistinguishable from "this pilot has nothing configured". So callers
 *    pass a `validate` predicate and we fail loudly on the shape instead.
 *
 * proxypulse relays quill's `{ data: … }` envelope, so the payload may arrive
 * either bare or wrapped; unwrap one optional layer before validating.
 */
function unwrap<T>(
  raw      : any,
  what     : string,
  validate?: (candidate: any) => boolean,
): InsightApiResult<T> {
  if (!raw || raw.error) {
    return { error: raw?.error ?? { message: `Failed to load ${what}` } }
  }

  const body      = raw.data ?? raw
  const candidate = body?.data !== undefined ? body.data : body

  if (candidate === undefined || candidate === null) {
    return { error: { message: `Empty response for ${what}` } }
  }
  if (validate && !validate(candidate)) {
    return { error: { message: `Response for ${what} was not in the expected shape` } }
  }
  return { data: candidate as T }
}

export const InsightApis = {

  /**
   * Catalog for every dropdown, chip and value control. Relayed verbatim by
   * proxypulse — do not filter or reorder it here.
   *
   * `normalizeInsightCatalog` is field renaming, not filtering: quill spreads
   * the raw appliance and appliance-category rows in without mapping them to
   * `{ code, displayName }` like it does the other sections. Nothing is dropped
   * except rows with no usable key. See the helper for the full shape note.
   */
  getCatalog : async (rc: RunContextApp): Promise<InsightApiResult<InsightCatalog>> => {
    const raw    = await rc.bffClient.getInsightCatalog()
    const result = unwrap<InsightCatalog>(raw, 'insight catalog',
      (c) => Array.isArray(c.derivedQuantities) && Array.isArray(c.types))

    return result.data ? { data : normalizeInsightCatalog(result.data) } : result
  },

  /**
   * Whole-pilot list, partitioned { draft, modified, published }.
   *
   * ⚠️ There are no paging, sort or search params — the caller merges the three
   * arrays and pages locally. Do NOT dedupe on `code`: it is unique per pilot,
   * not globally, so the same code legitimately recurs across pilots.
   */
  getInsights : async (
    rc                : RunContextApp,
    utilityDocumentId : string,
  ): Promise<InsightApiResult<GetInsightsResponse>> => {
    const raw = await rc.bffClient.getInsights(utilityDocumentId)
    return unwrap<GetInsightsResponse>(raw, 'insights',
      (d) => Array.isArray(d.draft) || Array.isArray(d.modified) || Array.isArray(d.published))
  },

  /**
   * Workflow table the QA-status dropdown renders transitions from. `statusLabels`
   * is where display text lives — `READY_FOR_QA` shows as "Ready for review".
   * Relayed verbatim; filtering it makes the FE and BE disagree.
   */
  getReviewWorkflow : async (rc: RunContextApp): Promise<InsightApiResult<InsightReviewWorkflow>> => {
    const raw = await rc.bffClient.getInsightReviewWorkflow()
    return unwrap<InsightReviewWorkflow>(raw, 'review workflow', (w) => Array.isArray(w))
  },

  /** quill derives `code` and `insightId`. A client-supplied value is rejected. */
  createInsight : async (
    rc   : RunContextApp,
    body : CreateInsightRequest,
  ): Promise<InsightApiResult<CreateInsightResponse>> => {
    const raw = await rc.bffClient.createInsight(body)
    return unwrap<CreateInsightResponse>(raw, 'created insight')
  },

  /**
   * Batch diff-then-patch. Rows carry only changed fields; `code`, `insightId`
   * and `editStatus` must be absent. `groupInfo`, `templateCriteria` and
   * `validationState` are merged server-side, so send whole objects for those.
   */
  updateAll : async (
    rc   : RunContextApp,
    body : UpdateAllRequest,
  ): Promise<InsightApiResult<UpdateAllResponse>> => {
    const raw = await rc.bffClient.updateInsights(body)
    return unwrap<UpdateAllResponse>(raw, 'insight update')
  },

  /** Resets `verificationStatus` to TODO server-side — refetch after this. */
  publishAll : async (
    rc   : RunContextApp,
    body : InsightBatchRequest,
  ): Promise<InsightApiResult<InsightBatchResponse>> => {
    const raw = await rc.bffClient.publishInsights(body)
    return unwrap<InsightBatchResponse>(raw, 'insight publish')
  },

  unpublishAll : async (
    rc   : RunContextApp,
    body : InsightBatchRequest,
  ): Promise<InsightApiResult<InsightBatchResponse>> => {
    const raw = await rc.bffClient.unpublishInsights(body)
    return unwrap<InsightBatchResponse>(raw, 'insight unpublish')
  },

  /** Reverts to the published version, or DELETES a never-published draft. */
  discardAllDrafts : async (
    rc   : RunContextApp,
    body : InsightBatchRequest,
  ): Promise<InsightApiResult<InsightBatchResponse>> => {
    const raw = await rc.bffClient.discardInsightDrafts(body)
    return unwrap<InsightBatchResponse>(raw, 'insight draft discard')
  },

  /**
   * Tag / untag recommendations. The response carries a FRESH `allowedTransitions`
   * for the insight — the cross-entity gate is evaluated per record, so re-render
   * the QA-status dropdown from it rather than keeping the pre-tag array.
   */
  tagRecommendations : async (
    rc   : RunContextApp,
    body : TagRecommendationsRequest,
  ): Promise<InsightApiResult<TagRecommendationsResponse>> => {
    const raw = await rc.bffClient.tagInsightRecommendations(body)
    return unwrap<TagRecommendationsResponse>(raw, 'recommendation tagging')
  },

  /* --- GenAI + validation (DA-2401) ------------------------------------- */

  /**
   * Stateless token/registry check. Relays quill's validator rather than
   * reimplementing it — the resolvability matrix, deprecated flags and
   * `supportedDateFormats` live in quill's `derived-quantity-variable` table and
   * a client-side copy would drift the moment a row changes.
   *
   * Complements, does not replace, the local `${…}` checks in
   * `insightCatalogHelpers`: those run per keystroke off the cached catalog,
   * this is the authoritative answer.
   */
  validateTemplateText : async (
    rc   : RunContextApp,
    body : { templateString: string; type?: string | null },
  ): Promise<InsightApiResult<TemplateTextValidation>> => {
    const raw = await rc.bffClient.validateInsightTemplateText(body)
    return unwrap<TemplateTextValidation>(raw, 'template validation',
      (v) => typeof v.valid === 'boolean')
  },

  /**
   * AI Writing Assistant. Candidates are token-validated server-side before
   * they are returned; invalid ones arrive flagged, so render `valid` rather
   * than assuming everything offered is usable.
   */
  generateAlternative : async (
    rc   : RunContextApp,
    body : GenerateAlternativeRequest,
  ): Promise<InsightApiResult<GenerateAlternativeResponse>> => {
    const raw = await rc.bffClient.generateInsightAlternative(body)
    return unwrap<GenerateAlternativeResponse>(raw, 'AI suggestions',
      (r) => Array.isArray(r.alternatives))
  },

  /** Advisory meaning-vs-classification check. Never gates a save. */
  semanticValidate : async (
    rc   : RunContextApp,
    body : { templateString: string; classification?: Record<string, unknown>; pilotId?: string; insightDocumentId?: string },
  ): Promise<InsightApiResult<SemanticValidateResponse>> => {
    const raw = await rc.bffClient.semanticValidateInsight(body)
    return unwrap<SemanticValidateResponse>(raw, 'semantic validation',
      (r) => r.verdict === 'PASS' || r.verdict === 'WARN' || r.verdict === 'FAIL')
  },
}
